import React, { forwardRef } from 'react';
import {
  ConfirmationLanguageCode,
  ConfirmationTranslation,
  RegistrationConfirmation,
} from '../../types/RegistrationConfirmation.type';
import './RegistrationConfirmationDocument.css';

type RegistrationConfirmationDocumentProps = {
  confirmation: RegistrationConfirmation;
  language?: ConfirmationLanguageCode;
};

const RegistrationConfirmationDocument = forwardRef<
  HTMLDivElement,
  RegistrationConfirmationDocumentProps
>(({ confirmation, language = 'vi' }, ref) => {
  const vietnamese = confirmation.template.translations.vi;
  const selected = confirmation.template.translations[language] || vietnamese;
  const translation: ConfirmationTranslation = {
    ...vietnamese,
    ...selected,
    labels: { ...vietnamese.labels, ...selected.labels },
    support: { ...vietnamese.support, ...selected.support },
    terms: selected.terms.length > 0 ? selected.terms : vietnamese.terms,
  };
  const isEnglish = language === 'en';
  const publicUrl = process.env.PUBLIC_URL || '';
  const logoSource = `${publicUrl}/Bluename_edited.avif`;
  const watermarkSource = `${publicUrl}/favicon.ico`;
  const product = confirmation.product;
  const payment = confirmation.payment;
  const labels = translation.labels;
  const support = translation.support;
  const title = isEnglish
    ? 'Service registration confirmation'
    : 'Xác nhận đăng ký dịch vụ';
  const description = isEnglish
    ? 'Thank you for registering a service package with Aquatics Hub. Please find your registration details below.'
    : 'Cảm ơn quý khách đã đăng ký gói dịch vụ tại Aquatics Hub. Dưới đây là thông tin chi tiết đăng ký của quý khách.';
  const customerSectionTitle = isEnglish
    ? 'Customer information'
    : 'Thông tin khách hàng';
  const serviceSectionTitle = isEnglish
    ? 'Service package details'
    : 'Chi tiết gói dịch vụ';
  const termsSectionTitle = isEnglish
    ? 'Terms and conditions'
    : 'Quy định & điều khoản';
  const supportTitle = (
    support.label || (isEnglish ? 'Support' : 'Kênh hỗ trợ')
  ).replace(/:$/, '');
  const usageValue =
    product.visitBalance !== null
      ? product.visitBalance
      : product.duration > 0
        ? product.duration
        : '—';
  const usageLabel =
    product.visitBalance !== null
      ? isEnglish
        ? 'visits'
        : 'lượt sử dụng'
      : isEnglish
        ? 'validity days'
        : 'ngày hiệu lực';
  const statusLabel =
    payment.statusLabel ||
    (isEnglish ? 'Registration confirmed' : 'Đã xác nhận');
  const workingHours = support.workingHours
    ? `${isEnglish ? 'Working hours' : 'Giờ làm việc'}: ${support.workingHours}`
    : '';

  return (
    <div ref={ref} className="registration-confirmation-print-root">
      <article className="registration-confirmation-document">
        <img
          className="registration-confirmation-watermark"
          src={watermarkSource}
          alt=""
          aria-hidden="true"
        />

        <header className="registration-confirmation-brand-header">
          <div className="registration-confirmation-logo-shell">
            <img
              className="registration-confirmation-logo"
              src={logoSource}
              alt="Aquatics Hub"
            />
          </div>
          <div className="registration-confirmation-brand-copy">
            <span className="registration-confirmation-brand-name">
              AQUATICS HUB
            </span>
            <span className="registration-confirmation-brand-subtitle">
              {isEnglish
                ? 'Aquatics sports and wellness center'
                : 'Trung tâm thể thao dưới nước'}
            </span>
          </div>
        </header>

        <div className="registration-confirmation-content">
          <section className="registration-confirmation-title-block">
            <h1 className="registration-confirmation-title">{title}</h1>
            <p className="registration-confirmation-description">
              {description}
            </p>
          </section>

          <section className="registration-confirmation-customer-card">
            <span className="registration-confirmation-eyebrow">
              {customerSectionTitle}
            </span>
            <div className="registration-confirmation-customer-grid">
              <div className="registration-confirmation-field">
                <span className="registration-confirmation-field-label">
                  {labels.fullName || (isEnglish ? 'Full name' : 'Họ và tên')}
                </span>
                <strong>{confirmation.customer.fullName}</strong>
              </div>
              <div className="registration-confirmation-field">
                <span className="registration-confirmation-field-label">
                  {labels.memberCode ||
                    (isEnglish ? 'Member code' : 'Mã hội viên')}
                </span>
                <strong>{confirmation.customer.memberCode}</strong>
              </div>
              <div className="registration-confirmation-field registration-confirmation-field-full">
                <span className="registration-confirmation-field-label">
                  {labels.phone ||
                    (isEnglish ? 'Phone number' : 'Số điện thoại')}
                </span>
                <strong>{confirmation.customer.phone || '---'}</strong>
              </div>
            </div>
          </section>

          <section className="registration-confirmation-service-section">
            <span className="registration-confirmation-eyebrow">
              {serviceSectionTitle}
            </span>
            <div className="registration-confirmation-service-card">
              <div className="registration-confirmation-service-main">
                <h2>{product.displayName || product.packageName}</h2>
                <div className="registration-confirmation-service-grid">
                  <div className="registration-confirmation-field">
                    <span className="registration-confirmation-field-label">
                      {labels.activationDate ||
                        (isEnglish ? 'Activation date' : 'Ngày kích hoạt')}
                    </span>
                    <strong>{product.activationDate}</strong>
                  </div>
                  <div className="registration-confirmation-field">
                    <span className="registration-confirmation-field-label">
                      {labels.expiryDate ||
                        (isEnglish ? 'Expiry date' : 'Ngày hết hạn')}
                    </span>
                    <strong>{product.expiryDate}</strong>
                  </div>
                  <div className="registration-confirmation-field">
                    <span className="registration-confirmation-field-label">
                      {labels.paymentMethod ||
                        (isEnglish
                          ? 'Payment method'
                          : 'Phương thức thanh toán')}
                    </span>
                    <strong>{payment.methodLabel || '---'}</strong>
                  </div>
                  <div className="registration-confirmation-field">
                    <span className="registration-confirmation-field-label">
                      {labels.paymentStatus ||
                        (isEnglish ? 'Status' : 'Trạng thái')}
                    </span>
                    <strong className="registration-confirmation-status-text">
                      {statusLabel}
                    </strong>
                  </div>
                  {product.serviceFor === 'GROUP' && (
                    <div className="registration-confirmation-field registration-confirmation-field-full">
                      <span className="registration-confirmation-field-label">
                        {isEnglish ? 'Quantity' : 'Số lượng'}
                      </span>
                      <strong>
                        {product.amount} {isEnglish ? 'adult(s)' : 'người lớn'}
                        {product.subAmount !== null
                          ? `, ${product.subAmount} ${
                              isEnglish ? 'child(ren)' : 'trẻ em'
                            }`
                          : ''}
                      </strong>
                    </div>
                  )}
                </div>
              </div>
              <div className="registration-confirmation-perforation" />
              <div className="registration-confirmation-usage-stub">
                <span className="registration-confirmation-usage-value">
                  {usageValue}
                </span>
                <span className="registration-confirmation-usage-label">
                  {usageLabel}
                </span>
              </div>
            </div>
          </section>

          <section className="registration-confirmation-terms-section">
            <span className="registration-confirmation-eyebrow">
              {termsSectionTitle}
            </span>
            <ol className="registration-confirmation-terms-list">
              {translation.terms.map((term, index) => (
                <li key={`${confirmation.registrationId}-term-${index}`}>
                  {term}
                </li>
              ))}
            </ol>
          </section>

          <section className="registration-confirmation-support-card">
            <span className="registration-confirmation-support-eyebrow">
              {supportTitle}
            </span>
            <strong>
              {support.channel || (isEnglish ? 'Reception support' : 'Lễ tân')}
              {support.phone ? `: ${support.phone}` : ''}
            </strong>
            {workingHours && <span>{workingHours}</span>}
            {support.additionalText && <span>{support.additionalText}</span>}
          </section>
        </div>
      </article>
    </div>
  );
});

RegistrationConfirmationDocument.displayName =
  'RegistrationConfirmationDocument';

export default RegistrationConfirmationDocument;
